/**
 * "Add directly" modal: create a creator or ambassador without an application.
 * On save, their Shopify discount code is minted immediately, the welcome
 * email goes out, and past sales with their code are backfilled.
 */
import { useState } from "react";
import { Button, Input, Label, Modal, ModalFooter, Select, useToast } from "@/components/ui";
import { affiliatesApi, type Affiliate } from "./api";
import { defaultDiscountCodePreview } from "./code-format";

export function AddDirectModal({
  defaultType,
  onClose,
  onAdded,
}: {
  defaultType: "creator" | "ambassador";
  onClose: () => void;
  onAdded: (affiliate: Affiliate) => void;
}) {
  const [type, setType] = useState<"creator" | "ambassador">(defaultType);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [socialLinks, setSocialLinks] = useState("");
  const [discountPct, setDiscountPct] = useState("15");
  const [commissionPct, setCommissionPct] = useState("10");
  const [codeDraft, setCodeDraft] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    if (!firstName.trim()) return toast.error("Enter their first name");
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error("Enter a valid email");
    const dp = Number(discountPct);
    const cr = Number(commissionPct);
    if (!(dp > 0 && dp <= 100)) return toast.error("Customer discount must be 1-100%");
    if (!(cr > 0 && cr <= 100)) return toast.error("Commission must be 1-100%");

    setBusy(true);
    try {
      const res = await affiliatesApi.addDirect({
        type,
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        email: email.trim().toLowerCase(),
        social_links: socialLinks.trim() || undefined,
        customer_discount_pct: dp,
        commission_rate: cr / 100,
        code: codeDraft.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(`${type === "ambassador" ? "Ambassador" : "Creator"} added - their code is ${res.affiliate.discount_code}`);
      onAdded(res.affiliate);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add - check the store connection");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Add ${type === "ambassador" ? "an ambassador" : "a creator"} directly`}>
      <div className="space-y-4">
        <p className="text-sm text-zinc-500">
          No application needed. On save, their personal Shopify code is created, the welcome email goes out,
          and any past sales that used their code are credited automatically.
        </p>

        <div>
          <Label>Type</Label>
          <Select value={type} onChange={(e) => setType(e.target.value as "creator" | "ambassador")}>
            <option value="creator">Creator (applied or referred)</option>
            <option value="ambassador">Ambassador (hand-picked influencer)</option>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={80} />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={80} />
          </div>
        </div>

        <div>
          <Label>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} placeholder="Where the welcome email goes" />
        </div>

        <div>
          <Label>Social links (optional)</Label>
          <Input value={socialLinks} onChange={(e) => setSocialLinks(e.target.value)} maxLength={2000} placeholder="instagram.com/..." />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Customer discount (%)</Label>
            <Input type="number" min={1} max={100} value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
          </div>
          <div>
            <Label>Commission (%)</Label>
            <Input type="number" min={1} max={100} value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} />
          </div>
        </div>

        <div>
          <Label>Discount code (optional - defaults to first name + last initial)</Label>
          <Input value={codeDraft} onChange={(e) => setCodeDraft(e.target.value)} placeholder={firstName.trim() ? defaultDiscountCodePreview(firstName, lastName) : "CODE"} maxLength={40} />
        </div>

        <div>
          <Label>Notes (optional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
        </div>
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} disabled={busy}>{busy ? "Creating code..." : "Add & create code"}</Button>
      </ModalFooter>
    </Modal>
  );
}
